import { Lightbulb, ArrowRight, BookOpen, Sparkles, TrendingUp } from 'lucide-react';
import ScrollReveal from './ScrollReveal';

const topics = [
  { icon: BookOpen, title: 'SEO Fundamentals', desc: 'Meta tags, headings, structured data, and crawl optimization' },
  { icon: Sparkles, title: 'AI Readiness', desc: 'llms.txt, ai.txt, AI Overviews, and LLM visibility strategies' },
  { icon: TrendingUp, title: 'Advanced Techniques', desc: 'JavaScript SEO, RENDERED JavaScript rendering, GEO scoring, and more' },
];

const LearningBanner = () => (
  <section id="learning" className="py-16 md:py-20" aria-label="Learning Center">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <ScrollReveal>
        <div className="relative overflow-hidden rounded-3xl bg-cv-navy cv-emboss shadow-2xl shadow-cv-navy/25 border border-cv-gold/40">
          {/* Top accent bar — Learning Center brand mark */}
          <div className="h-1.5 bg-cv-gold" />

          <div className="relative z-10 p-8 sm:p-12 lg:p-14">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              {/* Left: content */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cv-gold rounded-full text-cv-navy text-xs font-bold uppercase tracking-wider mb-5">
                  <Lightbulb className="w-3.5 h-3.5" /> Free Knowledge Hub
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight">
                  Learning Center
                </h2>
                <p className="text-white/85 text-lg leading-relaxed max-w-xl mb-6">
                  Daily SEO tips, AI readiness guides, and expert insights — powered by our AI engine.
                  Stay ahead of search algorithm changes and make your website visible to both search engines and AI models.
                </p>
                <p className="text-white/70 text-sm sm:text-base mb-6">
                  New: practical playbooks for planning and scaling Domain Analysis workflows across large sites.
                </p>

                {/* Topic cards — white panels, navy text, gold icon accent */}
                <div className="grid sm:grid-cols-3 gap-3 mb-8">
                  {topics.map((t) => (
                    <div key={t.title} className="bg-white rounded-xl p-3.5 text-left border-t-2 border-cv-gold">
                      <t.icon className="w-5 h-5 text-cv-gold mb-1.5" />
                      <h3 className="text-sm font-bold text-cv-navy mb-0.5">{t.title}</h3>
                      <p className="text-xs text-cv-navy/70 leading-snug">{t.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  <a
                    href="/learning"
                    className="inline-flex items-center gap-2 px-7 py-3.5 bg-cv-gold text-cv-navy font-bold text-sm rounded-xl cv-emboss hover:bg-cv-gold/90 transition-all"
                  >
                    Explore Learning Center <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Right: visual */}
              <div className="shrink-0 w-56 h-56 lg:w-64 lg:h-64 relative">
                <div className="relative bg-white rounded-3xl w-full h-full flex flex-col items-center justify-center p-6 cv-emboss border-t-4 border-cv-gold">
                  <span className="text-7xl mb-3">💡</span>
                  <span className="text-2xl font-extrabold text-cv-navy">Daily Tips</span>
                  <span className="text-sm text-cv-navy/70 mt-1">AI-generated</span>
                  <span className="mt-3 px-3 py-1 bg-cv-orange text-white text-xs font-bold rounded-full animate-pulse">NEW CONTENT DAILY</span>
                </div>
              </div>
            </div>

            {/* Crawler-friendly hidden semantic content */}
            <div className="sr-only" itemScope itemType="https://schema.org/Course">
              <meta itemProp="name" content="Crawler Vision Learning Center" />
              <meta itemProp="description" content="Free SEO and AI readiness learning hub with daily tips on technical SEO, meta optimization, structured data, JavaScript rendering, AI crawler readiness, LLM visibility, and GEO scoring. Powered by AI-generated content." />
              <meta itemProp="provider" content="Crawler Vision" />
              <meta itemProp="url" content="https://crawlervision.com/learning" />
              <meta itemProp="isAccessibleForFree" content="true" />
              <link itemProp="educationalLevel" href="https://schema.org/Beginner" />
              <span itemProp="about">SEO Analysis, AI Crawler Readiness, Technical SEO, LLM Visibility, GEO Scoring, AIO Readiness, RENDERED JavaScript Rendering, Meta Optimization, Structured Data, JavaScript SEO</span>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>
  </section>
);

export default LearningBanner;
